<script setup lang="ts">
import type { NavigationMenuItem } from '@nuxt/ui'
import type { AccountListItem } from '#shared/types/admin'

const { isDemoMode } = useDemo()
const { isAdmin } = useAdminAuth()
const { isAuthenticated } = useAuth()
const { isImpersonating, isInitializing, impersonatedCustomer, setCustomer } = useImpersonation()

// Show a global warning banner when the selected account is a test account
const showTestAccountBanner = computed(() => isImpersonating.value && impersonatedCustomer.value.isTestAccount)

// Account switcher visible in sidebar for any authenticated user (admin or regular), excluding demo
const showAccountSwitcherSection = computed(() => (isAdmin.value || isAuthenticated.value) && !isDemoMode.value)

const open = ref(false)
const impersonationModalOpen = ref(false)
const cookieConsentRef = useTemplateRef<{ open: () => void }>('cookieConsent')

// Auto-open account selector for any authenticated user (admin or regular)
// that hasn't selected an account yet (no cached impersonation)
function maybeAutoOpenAccountSelector() {
  if (isDemoMode.value || isImpersonating.value) return
  if (isAdmin.value || isAuthenticated.value) {
    impersonationModalOpen.value = true
  }
}

onMounted(() => {
  maybeAutoOpenAccountSelector()
})

// Re-evaluate when auth state syncs after mount (admin login, user login)
watch([isAdmin, isAuthenticated], () => {
  maybeAutoOpenAccountSelector()
})

const links = [{
  label: 'Mission Control',
  icon: 'i-lucide-radio',
  to: '/',
  onSelect: () => {
    open.value = false
  }
}, {
  label: 'Transactions',
  icon: 'i-lucide-banknote',
  to: '/transactions',
  onSelect: () => {
    open.value = false
  }
}, {
  label: 'Wallets',
  icon: 'i-lucide-wallet',
  to: '/wallets',
  onSelect: () => {
    open.value = false
  }
}, {
  label: 'Merchants',
  icon: 'i-lucide-store',
  to: '/merchants',
  onSelect: () => {
    open.value = false
  }
}, {
  label: 'Account',
  icon: 'i-lucide-building-2',
  to: '/account',
  onSelect: () => {
    open.value = false
  }
}] satisfies NavigationMenuItem[]

function openCookieSettings() {
  cookieConsentRef.value?.open()
}

async function handleAccountSelect(account: AccountListItem) {
  // Close modal first, then switch account (which shows overlay during data refresh)
  impersonationModalOpen.value = false
  await setCustomer(account)
}
</script>

<template>
  <UDashboardGroup unit="rem">
    <UDashboardSidebar
      id="default"
      v-model:open="open"
      collapsible
      resizable
      class="bg-elevated/25"
      :ui="{ footer: 'lg:border-t lg:border-default' }"
    >
      <template #header="{ collapsed }">
        <div class="flex items-center w-full gap-2">
          <LayoutAppLogo
            :collapsed="collapsed"
            :height="30"
          />
          <UBadge
            v-if="isDemoMode && !collapsed"
            color="neutral"
            variant="solid"
          >
            DEMO
          </UBadge>
        </div>
      </template>

      <template #default="{ collapsed }">
        <!-- Account Switcher Section (visible for any authenticated user, hidden in demo) -->
        <div v-if="showAccountSwitcherSection && !collapsed" class="px-3 pb-3">
          <AdminImpersonationBadge
            v-if="isImpersonating"
            @switch-account="impersonationModalOpen = true"
          />
          <AdminSelectAccountPrompt
            v-else
            @select-account="impersonationModalOpen = true"
          />
        </div>

        <UNavigationMenu
          :collapsed="collapsed"
          :items="links"
          orientation="vertical"
          :ui="{
            link: collapsed ? 'min-h-11 py-3 text-base flex items-center justify-center' : 'min-h-11 px-3 py-3 text-base',
            linkLeadingIcon: 'text-lg',
            linkLabel: 'text-base'
          }"
        />
      </template>

      <template #footer="{ collapsed }">
        <LayoutAppUserMenu
          :collapsed="collapsed"
          @open-cookie-settings="openCookieSettings"
          @switch-account="impersonationModalOpen = true"
        />
      </template>
    </UDashboardSidebar>

    <!-- Main content area with relative positioning for overlay.
         min-w-0 is required: as a flex item inside UDashboardGroup, without it the column
         inflates to its children's intrinsic width (e.g. the test-account banner text),
         pushing the whole console wider than the viewport on mobile. -->
    <div class="relative flex-1 flex flex-col min-h-0 min-w-0">
      <LayoutTestAccountBanner v-if="showTestAccountBanner" />
      <main class="flex-1 overflow-y-auto custom-scrollbar">
        <div class="max-w-screen-2xl mx-auto w-full">
          <slot />
        </div>
      </main>

      <!-- Session restoration overlay (impersonation) -->
      <div v-if="isInitializing" class="admin-restore-overlay">
        <div class="flex flex-col items-center gap-5">
          <UIcon
            name="i-svg-spinners:blocks-shuffle-3"
            class="size-8 text-primary"
          />
          <div class="text-center space-y-1">
            <p class="text-sm font-medium text-default">
              Restoring session...
            </p>
          </div>
        </div>
      </div>

      <!-- Admin account switching overlay (for subsequent switches) -->
      <AdminSwitchingOverlay v-if="isAdmin" />
    </div>

    <BaseCookieConsent ref="cookieConsent" />

    <!-- Admin Impersonation Modal -->
    <AdminImpersonationModal
      v-model="impersonationModalOpen"
      @select="handleAccountSelect"
    />
  </UDashboardGroup>
</template>
